import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type {
  BlogAuthor,
  BlogCategory,
  BlogPostWithRelations,
} from "@/types/blog";

/**
 * All blog posts with joined author + category for the admin list. Uses
 * SELECT *, blog_authors(...), blog_categories(...) so the page can
 * render author names and category slugs inline. Mutations should
 * invalidate ["admin", "blog_posts"].
 */
export function useAdminBlogPosts() {
  return useQuery({
    queryKey: ["admin", "blog_posts"],
    staleTime: 30_000,
    queryFn: async (): Promise<BlogPostWithRelations[]> => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*, blog_authors(name, avatar_url), blog_categories(name, slug)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data as BlogPostWithRelations[] | null) ?? [];
    },
  });
}

/**
 * Single blog post by id, used by the admin editor. Includes author +
 * category joins so the edit form can pre-populate the dropdowns.
 */
export function useAdminBlogPost(id: string | null | undefined) {
  return useQuery({
    queryKey: ["admin", "blog_posts", "detail", id],
    enabled: Boolean(id),
    queryFn: async (): Promise<BlogPostWithRelations | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*, blog_authors(name, avatar_url), blog_categories(name, slug)")
        .eq("id", id)
        .single();
      if (error) throw error;
      return (data as BlogPostWithRelations | null) ?? null;
    },
  });
}

/**
 * Public blog post by slug, with author + category + tags joined. Skips
 * the status=published filter when preview is true so admins can view
 * drafts via ?preview=true.
 */
export function usePublicBlogPostBySlug(
  slug: string | null | undefined,
  preview: boolean = false,
) {
  return useQuery({
    queryKey: ["blog_posts", "public-by-slug", slug, preview],
    enabled: Boolean(slug),
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<BlogPostWithRelations | null> => {
      if (!slug) return null;
      let query = supabase
        .from("blog_posts")
        .select("*, blog_authors(name, slug, avatar_url, bio), blog_categories(name, slug), blog_post_tags(blog_tags(name, slug))")
        .eq("slug", slug);
      if (!preview) query = query.eq("status", "published");
      const { data, error } = await query.maybeSingle();
      if (error) throw error;
      return (data as BlogPostWithRelations | null) ?? null;
    },
  });
}

/**
 * Single blog post by slug, public read. Used by the public /blog/:slug
 * page. staleTime longer here since published blog posts rarely change.
 */
export function usePublishedBlogPost(slug: string | null | undefined) {
  return useQuery({
    queryKey: ["blog_posts", "by-slug", slug],
    enabled: Boolean(slug),
    staleTime: 5 * 60_000, // 5 minutes
    queryFn: async (): Promise<BlogPostWithRelations | null> => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*, blog_authors(*), blog_categories(*)")
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();
      if (error) throw error;
      return (data as BlogPostWithRelations | null) ?? null;
    },
  });
}

/**
 * All blog authors. Used by admin list + editor dropdowns.
 */
export function useBlogAuthors() {
  return useQuery({
    queryKey: ["blog_authors"],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<BlogAuthor[]> => {
      const { data, error } = await supabase
        .from("blog_authors")
        .select("*")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data as BlogAuthor[] | null) ?? [];
    },
  });
}

/**
 * All blog categories. Used by admin list + editor dropdowns.
 */
export function useBlogCategories() {
  return useQuery({
    queryKey: ["blog_categories"],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<BlogCategory[]> => {
      const { data, error } = await supabase
        .from("blog_categories")
        .select("*")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data as BlogCategory[] | null) ?? [];
    },
  });
}
